import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Paper,
  TextField,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  Avatar,
  Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpacityIcon from '@mui/icons-material/Opacity';
import ShieldIcon from '@mui/icons-material/Shield';
import SpeedIcon from '@mui/icons-material/Speed';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import RecyclingIcon from '@mui/icons-material/Recycling';
import SettingsIcon from '@mui/icons-material/Settings';
import { styled, alpha } from '@mui/material/styles';

// Styled components
const GlassPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.spacing(3),
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 15px 50px rgba(0, 0, 0, 0.12)',
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '6px',
    background: 'linear-gradient(90deg, #00C6CF 0%, #4FACFE 100%)',
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 198, 207, 0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  overflow: 'hidden',
  border: '1px solid rgba(0, 198, 207, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0, 198, 207, 0.12)',
  }
}));

const FeatureAvatar = styled(Avatar)(({ theme, bgcolor }) => ({
  backgroundColor: bgcolor || alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  width: 56,
  height: 56,
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 198, 207, 0.15)',
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  transition: 'transform 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'translateX(5px)',
  },
  '& svg': {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
    fontSize: '1.5rem',
  }
}));

const CountdownBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    gap: theme.spacing(3),
  }
}));

const TimeUnit = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: '90px',
  padding: theme.spacing(2, 1),
  borderRadius: theme.spacing(2),
  background: 'linear-gradient(135deg, rgba(0, 198, 207, 0.08) 0%, rgba(79, 172, 254, 0.08) 100%)',
  boxShadow: '0 8px 32px rgba(0, 198, 207, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 40px rgba(0, 198, 207, 0.15)',
  },
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #00C6CF 0%, #4FACFE 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  fontWeight: 800,
}));

const AnimatedWaterDrop = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  width: '300px',
  height: '300px',
  borderRadius: '80% 0 55% 50% / 55% 0 80% 50%',
  background: 'linear-gradient(135deg, rgba(0, 198, 207, 0.2), rgba(79, 172, 254, 0.2))',
  filter: 'blur(40px)',
  zIndex: -1,
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '50px',
  fontWeight: 600,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
  },
}));

const DomesticRO = () => {
  const [email, setEmail] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  
  // Calculate countdown to a date 30 days from now
  const launchDate = new Date();
  launchDate.setDate(launchDate.getDate() + 30);
  
  const [countdown, setCountdown] = React.useState({
    days: 30,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const difference = launchDate.getTime() - now.getTime();
      
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
      
      if (difference <= 0) {
        clearInterval(interval);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [launchDate]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the email to your backend
    console.log('Notification email:', email);
    setSubmitted(true);
    // Reset form after submission
    setTimeout(() => {
      setEmail('');
      setSubmitted(false);
    }, 3000);
  };
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 12,
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <AnimatedWaterDrop
        initial={{ x: -150, y: -50, opacity: 0.5 }}
        animate={{ 
          x: [-150, -120, -150],
          y: [-50, -30, -50],
          opacity: [0.5, 0.7, 0.5],
        }}
        transition={{ 
          repeat: Infinity,
          duration: 15,
          ease: 'easeInOut'
        }}
        style={{ top: '10%', left: '5%' }}
      />
      
      <AnimatedWaterDrop
        initial={{ x: 350, y: 250, opacity: 0.3 }}
        animate={{ 
          x: [350, 380, 350],
          y: [250, 230, 250],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ 
          repeat: Infinity,
          duration: 18,
          ease: 'easeInOut'
        }}
        style={{ bottom: '10%', right: '5%' }}
      />
      
      <AnimatedWaterDrop
        initial={{ x: 150, y: -150, opacity: 0.4 }}
        animate={{ 
          x: [150, 180, 150],
          y: [-150, -180, -150],
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{ 
          repeat: Infinity,
          duration: 20,
          ease: 'easeInOut'
        }}
        style={{ top: '5%', right: '15%' }}
      />
      
      <Container maxWidth="lg">
        {/* Header with Coming Soon Badge */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <StyledChip 
              icon={<NotificationsActiveIcon />} 
              label="COMING SOON" 
              sx={{ mb: 3, py: 2.5, px: 1, fontSize: '1rem' }}
            />
            
            <GradientText variant="h1" component="h1" sx={{ mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Domestic RO Solutions
            </GradientText>
            
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'text.secondary',
                mb: 5,
                maxWidth: '800px',
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.6
              }}
            >
              Experience pure, healthy water at home with our advanced Reverse Osmosis technology
            </Typography>
          </motion.div>
        </Box>
        
        {/* Countdown Timer */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <CountdownBox>
            <TimeUnit>
              <Typography variant="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {countdown.days}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Days
              </Typography>
            </TimeUnit>
            
            <TimeUnit>
              <Typography variant="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {countdown.hours}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Hours
              </Typography>
            </TimeUnit>
            
            <TimeUnit>
              <Typography variant="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {countdown.minutes}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Minutes
              </Typography>
            </TimeUnit>
            
            <TimeUnit>
              <Typography variant="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {countdown.seconds}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Seconds
              </Typography>
            </TimeUnit>
          </CountdownBox>
        </motion.div>
        
        {/* Main Content */}
        <Grid container spacing={6} sx={{ mt: 4 }}>
          {/* Left Side - Features */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <GlassPaper elevation={0}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
                  Revolutionary Water Purification
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FeatureCard>
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <FeatureAvatar>
                          <OpacityIcon />
                        </FeatureAvatar>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          5-Stage Filtration
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Advanced multi-stage purification removes contaminants down to 0.0001 microns
                        </Typography>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FeatureCard>
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <FeatureAvatar bgcolor={alpha('#4FACFE', 0.1)} sx={{ color: '#4FACFE' }}>
                          <ShieldIcon />
                        </FeatureAvatar>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          Health Protection
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Removes 99.9% of harmful contaminants including lead, chlorine and bacteria
                        </Typography>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FeatureCard>
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <FeatureAvatar bgcolor={alpha('#00C6CF', 0.1)}>
                          <PhoneAndroidIcon />
                        </FeatureAvatar>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          Smart Controls
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Monitor water quality and filter life with our intuitive smartphone app
                        </Typography>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FeatureCard>
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <FeatureAvatar bgcolor={alpha('#4FACFE', 0.1)} sx={{ color: '#4FACFE' }}>
                          <RecyclingIcon />
                        </FeatureAvatar>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          Eco-Friendly
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Reduce plastic waste with our sustainable water purification solution
                        </Typography>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Additional Features
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FeatureItem>
                        <SpeedIcon />
                        <Typography variant="body2">Energy-Efficient Operation</Typography>
                      </FeatureItem>
                      
                      <FeatureItem>
                        <SettingsIcon />
                        <Typography variant="body2">Easy Installation & Maintenance</Typography>
                      </FeatureItem>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FeatureItem>
                        <WaterDropIcon />
                        <Typography variant="body2">Compact & Elegant Design</Typography>
                      </FeatureItem>
                      
                      <FeatureItem>
                        <ShieldIcon />
                        <Typography variant="body2">5-Year Extended Warranty</Typography>
                      </FeatureItem>
                    </Grid>
                  </Grid>
                </Box>
              </GlassPaper>
            </motion.div>
          </Grid>
          
          {/* Right Side - Notification Form */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <GlassPaper elevation={0}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <WaterDropIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                    Be the First to Know
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Sign up to receive exclusive early access and special launch pricing for our revolutionary Domestic RO system.
                  </Typography>
                </Box>
                
                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 5 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="primary" />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(0, 198, 207, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        py: 1.5,
                        fontSize: '1.1rem'
                      }
                    }}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                      borderRadius: '12px',
                      py: 2,
                      background: 'linear-gradient(90deg, #00C6CF 0%, #4FACFE 100%)',
                      boxShadow: '0 8px 25px rgba(0, 198, 207, 0.3)',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      transition: 'all 0.3s',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #4FACFE 0%, #00C6CF 100%)',
                        boxShadow: '0 12px 30px rgba(0, 198, 207, 0.4)',
                        transform: 'translateY(-3px)',
                      }
                    }}
                  >
                    {submitted ? 'Thank You! We\'ll Be In Touch' : 'Notify Me When Available'}
                  </Button>
                </Box>
                
                <Divider sx={{ my: 4 }} />
                
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Why Choose Our Domestic RO?
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 3, 
                        borderRadius: 2, 
                        bgcolor: alpha('#00C6CF', 0.05),
                        border: `1px solid ${alpha('#00C6CF', 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <Avatar sx={{ bgcolor: alpha('#00C6CF', 0.2), color: '#00C6CF' }}>
                          <ShieldIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#00C6CF' }}>
                            Health Benefits
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Removes up to 99.9% of contaminants for healthier drinking water
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 3, 
                        borderRadius: 2, 
                        bgcolor: alpha('#4FACFE', 0.05),
                        border: `1px solid ${alpha('#4FACFE', 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <Avatar sx={{ bgcolor: alpha('#4FACFE', 0.2), color: '#4FACFE' }}>
                          <AccessTimeIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4FACFE' }}>
                            Pre-Order Opening Soon
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Be among the first to experience our revolutionary system with exclusive early-bird pricing
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </GlassPaper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DomesticRO;
